import "./BiomarkerListTableComponent.scss";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {ITableColumn} from "../../../shared/models/table.model";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import {CommonService} from "../../../shared/services";
import {ILocation} from "../../../shared/models/location.model";
import commonService from "../../../shared/services/common.service";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import React, {useCallback} from "react";
import SwitchComponent from "../../../shared/components/core/switch/switchComponent";
import {Link} from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {APIConfig, ImageConfig} from "../../../constants";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {BioMarker} from "../../../shared/models/biomarker.model";


interface BiomarkerListTableComponentProps {
    filterData?: {};
    isColumnFilterVisible: boolean;
    moduleName: string;
}

const BiomarkerListTableComponent = (props: BiomarkerListTableComponentProps) => {
    const {filterData, isColumnFilterVisible, moduleName} = props;

    const {currentUser} = useSelector((state: IRootReducerState) => state.account);


    const columns: ITableColumn[] = [
        {
            id: 'id',
            label: 'ID',
            isDefaultColumn: true,
            width: "100",
            fixed: "left",
            content: (item: BioMarker) => (
                <>
                    {item?.id}
                </>
            )

        },
        {
            id: 'name',
            label: 'Biomarker Name',
            isDefaultColumn: true,
            width: "250",
            fixed: "left",
            content: (item: BioMarker) => (
                <>
                    {
                        (item?.name).length > 20 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                placement={"top"}
                                arrow={true}
                            >
                                <>
                                    <div className={"ellipses-for-table-data"}>
                                        {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                    </div>
                                </>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'code',
            label: 'Biomarker Code',
            isDefaultColumn: false,
            width: "150",
            align: "center",
        },
        {
            id: 'sampleType',
            label: 'Sample Type',
            isDefaultColumn: true,
            width: "150",
            align: "center",
            content: (item: BioMarker) => (
                <>
                    {item?.sampleType
                        ?
                        commonService.capitalizeFirstLetterOfEachWord(item?.sampleType)
                        : "-"}
                </>
            )
        },
        {
            id: "sampleCollectionDeviceName",
            label: "Sample Collection Device",
            isDefaultColumn: false,
            width: "170",
            content: (item: BioMarker) => (
                <>
                    {item?.sampleCollectionDeviceName
                        ? item?.sampleCollectionDeviceName
                        : "-"}
                </>
            )
        },
        {
            id: 'addedDateAndTime',
            label: 'Added Date & Time',
            isDefaultColumn: false,
            width: "170",
            content: (item: BioMarker) => (
                <>
                    {item?.createdAt
                        ? commonService.formatDate(item?.createdAt, "DD/MM/YYYY hh:mm A")
                        : "-"}
                </>
            )
        },
        {
            id: 'addedBy',
            label: 'Added by',
            isDefaultColumn: false,
            width: "150",
            content: (item: BioMarker) => (
                <>
                    {commonService.getFullName(item?.createdByDetails).length > 20 ?
                        <TooltipComponent
                            tooltip={commonService.getFullName(item?.createdByDetails)}
                            placement={"top"}
                            arrow={true}
                        >
                            <>
                                <div className={"ellipses-for-table-data"}>
                                    {commonService.getFullName(item?.createdByDetails)}
                                </div>
                            </>
                        </TooltipComponent> :
                        <>
                            {commonService.getFullName(item?.createdByDetails)}
                        </>
                    }
                </>
            )
        },
        {
            id: "status",
            label: "Biomarker Status",
            isDefaultColumn: true,
            width: "150",
            align: 'center',
            content: (item: BioMarker) => (
                item.status ?
                    <>
                        {item?.status ? <>
                            <ChipComponent className={`min-width-60  ${item?.status}`}
                                           label={CommonService.capitalizeFirstLetter(item?.status)}
                                           variant={'outlined'}/>
                        </> : <>-</>
                        }
                    </> : <>NA</>
            )
        },
        {
            id: 'isActive',
            label: commonService.checkAccess('Biomarker',
                "biomarkerToggle", currentUser?.userAccessModules) ?
                'Active/Inactive' : "",
            width: commonService.checkAccess('Biomarker',
                "biomarkerToggle", currentUser?.userAccessModules) ?
                "150" : "10",
            align: "center",
            isDefaultColumn: true,
            content: (item: ILocation, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('Biomarker',
                            "biomarkerToggle", currentUser?.userAccessModules) ?
                            <SwitchComponent
                                size={"small"}
                                checked={item.isActive}
                                disabled={(item?.status === 'draft')}
                                onUpdate={() => handleBiomarkerToggle(item, index)}/> :
                            <></>
                    }
                </React.Fragment>
            )
        },
        {
            id: 'actions',
            label: '',
            isDefaultColumn: true,
            align: "center",
            width: "100",
            hideInColumnPreferences: true,
            fixed: "right",
            content: (item: ILocation, index: any) => (
                <React.Fragment>
                    <div className="table-actions" id={`view_biomarker_` + index}>
                        <Link to={CommonService._routeConfig.BiomarkerDetailsRoute(item?.id)}>
                            <div className="table-action">
                                <ChevronRightIcon/>
                            </div>
                        </Link>
                    </div>
                </React.Fragment>
            )
        },
    ];

    const onToggleBioMarker = useCallback((item: any, index: any) => {
        CommonService._biomarker.toggleBiomarker(item.id)
            .then((response: IAPIResponseType<any>) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: moduleName
                });
                CommonService._alert.showToast(response?.message || `Biomarker has been  ${item?.isActive ? "deactivated" : "activated"}`, "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the biomarker`, "error");
            });
    }, [moduleName]);

    const handleBiomarkerToggle = useCallback((item: any, index: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this biomarker?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: 'primary'
            },
            no: {
                text: "Cancel",
                color: 'primary'
            }
        })
            .then(() => {
                onToggleBioMarker(item, index);
            })
            .catch(() => {
            })
    }, [onToggleBioMarker]);

    return (
        <div className="table-list-container">
            <TableWrapperV2Component
                isPaginated={true}
                columns={columns}
                url={APIConfig.GET_BIOMARKERS_LIST.URL}
                method={APIConfig.GET_BIOMARKERS_LIST.METHOD}
                extraPayload={filterData}
                isColumnFilterVisible={isColumnFilterVisible}
                moduleName={moduleName}
            />
        </div>
    );

};

export default BiomarkerListTableComponent;
